import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Loader2, Trash2, Plus, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AIChatWidget = ({ isLoggedIn }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showSessions, setShowSessions] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && isLoggedIn) {
      loadSessions();
    }
  }, [isOpen, isLoggedIn]);

  const getToken = () => localStorage.getItem('token');

  const loadSessions = async () => {
    try {
      const res = await fetch(`${API_URL}/api/ai/sessions`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSessions(data);
      }
    } catch (error) {
      console.error('Error loading sessions:', error);
    }
  };

  const loadSessionHistory = async (sessionId) => {
    try {
      const res = await fetch(`${API_URL}/api/ai/sessions/${sessionId}/history`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (res.ok) {
        const data = await res.json();
        setMessages(data.messages.map(m => ({
          role: m.role,
          content: m.content
        })));
        setCurrentSessionId(sessionId);
        setShowSessions(false);
      }
    } catch (error) {
      console.error('Error loading history:', error);
    }
  };

  const startNewSession = () => {
    setCurrentSessionId(null);
    setMessages([]);
    setShowSessions(false);
  };

  const deleteSession = async (sessionId, e) => {
    e.stopPropagation();
    try {
      const res = await fetch(`${API_URL}/api/ai/sessions/${sessionId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (res.ok) {
        setSessions(prev => prev.filter(s => s.id !== sessionId));
        if (currentSessionId === sessionId) {
          startNewSession();
        }
      }
    } catch (error) {
      console.error('Error deleting session:', error);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!message.trim() || isLoading) return;

    const userMessage = message.trim();
    setMessage('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const res = await fetch(`${API_URL}/api/ai/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        },
        body: JSON.stringify({
          message: userMessage,
          session_id: currentSessionId
        })
      });

      if (res.ok) {
        const data = await res.json();
        setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
        
        if (!currentSessionId) {
          setCurrentSessionId(data.session_id);
          loadSessions();
        }
      } else {
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: 'Lo siento, hubo un error al procesar tu mensaje. Por favor intenta de nuevo.' 
        }]);
      }
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Error de conexión. Por favor verifica tu conexión a internet.' 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isLoggedIn) return null;

  return (
    <>
      {/* Chat Button */}
      <button
        onClick={() => setIsOpen(true)}
        className={cn(
          "fixed bottom-24 right-6 z-50 w-14 h-14 rounded-full bg-accent text-white shadow-lg",
          "flex items-center justify-center hover:bg-accent/90 transition-all",
          "hover:scale-105 active:scale-95",
          isOpen && "hidden"
        )}
        data-testid="ai-chat-button"
      >
        <MessageCircle className="w-6 h-6" />
      </button>

      {/* Chat Window */}
      <div
        className={cn(
          "fixed bottom-24 right-6 z-50 w-[380px] h-[520px] bg-background rounded-2xl shadow-2xl",
          "flex flex-col overflow-hidden border border-border",
          "transform transition-all duration-300 origin-bottom-right",
          isOpen ? "scale-100 opacity-100" : "scale-0 opacity-0 pointer-events-none"
        )}
        data-testid="ai-chat-window"
      >
        {/* Header */}
        <div className="bg-primary text-white p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-accent flex items-center justify-center">
              <MessageCircle className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-semibold text-sm">Tutor IAASP</h3>
              <p className="text-xs text-white/70">Asistente virtual</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowSessions(!showSessions)}
              className="text-white hover:bg-white/20 h-8 w-8"
              data-testid="sessions-toggle"
            >
              <ChevronDown className={cn("w-4 h-4 transition-transform", showSessions && "rotate-180")} />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsOpen(false)}
              className="text-white hover:bg-white/20 h-8 w-8"
              data-testid="close-chat"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Sessions Dropdown */}
        {showSessions && (
          <div className="absolute top-16 left-0 right-0 bg-background border-b z-10 max-h-48 overflow-y-auto shadow-lg">
            <div className="p-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={startNewSession}
                className="w-full justify-start gap-2 text-sm"
                data-testid="new-session-btn"
              >
                <Plus className="w-4 h-4" />
                Nueva conversación
              </Button>
              {sessions.map(session => (
                <div
                  key={session.id}
                  onClick={() => loadSessionHistory(session.id)}
                  className={cn(
                    "flex items-center justify-between p-2 rounded-lg cursor-pointer hover:bg-muted",
                    currentSessionId === session.id && "bg-muted"
                  )}
                  data-testid={`session-${session.id}`}
                >
                  <span className="text-sm truncate flex-1">{session.title}</span>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={(e) => deleteSession(session.id, e)}
                    className="h-6 w-6 text-muted-foreground hover:text-destructive"
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-muted/30">
          {messages.length === 0 && (
            <div className="text-center text-muted-foreground py-8">
              <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p className="text-sm">¡Hola! Soy tu tutor virtual.</p>
              <p className="text-xs mt-1">¿En qué puedo ayudarte hoy?</p>
            </div>
          )}
          
          {messages.map((msg, idx) => (
            <div
              key={idx}
              className={cn(
                "flex",
                msg.role === 'user' ? "justify-end" : "justify-start"
              )}
            >
              <div
                className={cn(
                  "max-w-[85%] px-4 py-2.5 rounded-2xl text-sm",
                  msg.role === 'user'
                    ? "bg-accent text-white rounded-br-md"
                    : "bg-white text-foreground rounded-bl-md shadow-sm"
                )}
              >
                <p className="whitespace-pre-wrap">{msg.content}</p>
              </div>
            </div>
          ))}
          
          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-white px-4 py-3 rounded-2xl rounded-bl-md shadow-sm">
                <Loader2 className="w-5 h-5 animate-spin text-accent" />
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <form onSubmit={sendMessage} className="p-3 border-t bg-background">
          <div className="flex gap-2">
            <input
              type="text"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Escribe tu mensaje..."
              className="flex-1 px-4 py-2.5 rounded-full bg-muted text-sm focus:outline-none focus:ring-2 focus:ring-accent/50"
              disabled={isLoading}
              data-testid="chat-input"
            />
            <Button
              type="submit"
              size="icon"
              disabled={!message.trim() || isLoading}
              className="rounded-full bg-accent hover:bg-accent/90 h-10 w-10"
              data-testid="send-message-btn"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </form>
      </div>
    </>
  );
};
